'use client';

import React, { useCallback, useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faGripVertical,
  faMapMarkerAlt,
  faTrash,
  faLocationDot,
  faSpinner,
} from '@fortawesome/pro-solid-svg-icons';
import { PulseIconButton } from 'src/components';
import useStopSelection from 'src/hooks/routes/useStopSelection';
import { entityAddressToRouteStopAddress, type EntityAddress } from 'src/hooks/routes/utils';
import type { EnrichedRouteStop, RouteStopAddress } from 'src/hooks/routes/types';
import { useLazyGetEntityDetailsByIdQuery } from 'src/api/redux';
import AddressSelector from './AddressSelector';

interface StopListProps {
  stops: EnrichedRouteStop[];
  onReorder: (fromIndex: number, toIndex: number) => void;
  onRemove: (stopId: string) => void;
  isLoading?: boolean;
}

/**
 * StopList - Displays a draggable list of route stops
 *
 * Allows users to reorder stops via drag-and-drop and remove individual stops.
 */

const StopList: React.FC<StopListProps> = ({
  stops,
  onReorder,
  onRemove,
  isLoading = false,
}) => {
  const [draggedIndex, setDraggedIndex] = React.useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = React.useState<number | null>(null);
  const [addressSelectorStopId, setAddressSelectorStopId] = useState<
    string | null
  >(null);
  const [fetchingAddressesForStopId, setFetchingAddressesForStopId] = useState<
    string | null
  >(null);
  const [fetchedAddresses, setFetchedAddresses] = useState<
    Record<string, RouteStopAddress[]>
  >({});
  const selectorRef = useRef<HTMLDivElement>(null);

  const { updateStopAddress } = useStopSelection();
  const [fetchEntityDetails] = useLazyGetEntityDetailsByIdQuery();

  // Close address selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectorRef.current &&
        !selectorRef.current.contains(event.target as Node)
      ) {
        setAddressSelectorStopId(null);
      }
    };

    if (addressSelectorStopId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [addressSelectorStopId]);

  const handleAddressChange = useCallback(
    (stopId: string, address: RouteStopAddress) => {
      updateStopAddress(stopId, address);
      setAddressSelectorStopId(null);
    },
    [updateStopAddress]
  );

  const handleAddressButtonClick = useCallback(
    async (stop: EnrichedRouteStop) => {
      // If already open, close it
      if (addressSelectorStopId === stop.id) {
        setAddressSelectorStopId(null);
        return;
      }

      // If we already have addresses (from creation or previous fetch), just open
      const existingAddresses =
        stop.availableAddresses || fetchedAddresses[stop.id];
      if (existingAddresses && existingAddresses.length > 0) {
        setAddressSelectorStopId(stop.id);
        return;
      }

      // Need to fetch addresses
      setFetchingAddressesForStopId(stop.id);
      try {
        const result = await fetchEntityDetails({
          id: String(stop.providerId),
        }).unwrap();

        const addresses: RouteStopAddress[] = [];
        if (result?.primary_address) {
          addresses.push(
            entityAddressToRouteStopAddress(result.primary_address, true)
          );
        }
        if (result?.secondary_addresses) {
          result.secondary_addresses.forEach((addr: EntityAddress) => {
            addresses.push(entityAddressToRouteStopAddress(addr, false));
          });
        }

        setFetchedAddresses((prev) => ({ ...prev, [stop.id]: addresses }));
        setAddressSelectorStopId(stop.id);
      } catch {
        // Error handling - addresses won't be shown
      } finally {
        setFetchingAddressesForStopId(null);
      }
    },
    [addressSelectorStopId, fetchedAddresses, fetchEntityDetails]
  );

  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>, index: number) => {
      setDraggedIndex(index);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(index));
    },
    []
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>, index: number) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setDragOverIndex(index);
    },
    []
  );

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>, toIndex: number) => {
      e.preventDefault();
      const fromIndex = draggedIndex;
      setDraggedIndex(null);
      setDragOverIndex(null);

      if (fromIndex !== null && fromIndex !== toIndex) {
        onReorder(fromIndex, toIndex);
      }
    },
    [draggedIndex, onReorder]
  );

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  const handleRemove = useCallback(
    (stopId: string) => {
      onRemove(stopId);
    },
    [onRemove]
  );

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-14 bg-neutral-100 rounded-lg animate-pulse"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-1" data-testid="stop-list">
      {stops.map((stop, index) => {
        // User location stops are non-draggable, non-removable
        if (stop.isUserLocation) {
          return (
            <div
              key={stop.id}
              className="relative flex items-center gap-2 p-2 rounded-lg border border-blue-200 bg-blue-50"
              data-testid="stop-item-user-location"
            >
              {/* Spacer matching drag handle width */}
              <div className="w-6 h-6" />

              {/* Location indicator */}
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-700 shrink-0">
                <Icon icon={faLocationDot} className="w-3 h-3" />
              </div>

              {/* Stop info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-blue-900 truncate">
                  {stop.name}
                </p>
                <p className="text-xs text-blue-600">Starting point</p>
              </div>
            </div>
          );
        }

        const isAddressSelectorOpen = addressSelectorStopId === stop.id;
        const isFetchingAddresses = fetchingAddressesForStopId === stop.id;
        const availableAddresses =
          stop.availableAddresses || fetchedAddresses[stop.id] || [];

        return (
          <div
            key={stop.id}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            className={`
              relative flex items-center gap-2 p-2 rounded-lg border transition-colors cursor-move
              ${
                draggedIndex === index
                  ? 'opacity-50 border-sky-300 bg-sky-50'
                  : 'border-neutral-200 bg-white hover:border-neutral-300'
              }
              ${
                dragOverIndex === index && draggedIndex !== index
                  ? 'border-sky-500 bg-sky-50'
                  : ''
              }
            `}
            data-testid={`stop-item-${index}`}
          >
            {/* Drag handle */}
            <div
              className="flex items-center justify-center w-6 h-6 text-neutral-400 hover:text-neutral-600"
              aria-label="Drag to reorder"
              data-testid={`stop-drag-handle-${index}`}
            >
              <Icon icon={faGripVertical} className="w-3 h-3" />
            </div>

            {/* Stop number indicator */}
            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-sky-100 text-sky-700 text-xs font-semibold shrink-0">
              {index + 1}
            </div>

            {/* Stop info */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-neutral-900 truncate">
                {stop.name}
              </p>
              {stop.address && (
                <p className="text-xs text-neutral-500 flex items-center gap-1">
                  <Icon
                    icon={faMapMarkerAlt}
                    className="w-2.5 h-2.5 shrink-0"
                  />
                  <span className="truncate">{stop.address}</span>
                </p>
              )}
            </div>

            {/* Change address button - always show, fetch on demand */}
            <div
              className="relative"
              ref={isAddressSelectorOpen ? selectorRef : null}
            >
              <PulseIconButton
                variant="soft"
                color="neutral"
                size="sm"
                onClick={() => handleAddressButtonClick(stop)}
                disabled={isFetchingAddresses}
                icon={isFetchingAddresses ? faSpinner : faLocationDot}
                className={isFetchingAddresses ? 'animate-spin' : ''}
                aria-label="Change address"
                aria-expanded={isAddressSelectorOpen}
                data-testid={`stop-change-address-${index}`}
                tooltip="Change address"
              />
              {isAddressSelectorOpen && availableAddresses.length > 0 && (
                <AddressSelector
                  addresses={availableAddresses}
                  selectedAddressKey={stop.addressKey}
                  onSelect={(address) => handleAddressChange(stop.id, address)}
                  onClose={() => setAddressSelectorStopId(null)}
                />
              )}
            </div>

            {/* Remove button */}
            <PulseIconButton
              variant="soft"
              color="danger"
              size="sm"
              onClick={() => handleRemove(stop.id)}
              icon={faTrash}
              aria-label="Remove stop"
              data-testid={`stop-remove-${index}`}
              tooltip="Remove stop"
            />
          </div>
        );
      })}
    </div>
  );
};

export default StopList;
